/**
 * API Endpoint definitions for the Admin Test Page
 *
 * This file contains structured definitions of all API endpoints
 * from the REST API audit, organized by category.
 */

// USER MANAGEMENT ENDPOINTS
export const userApiEndpoints = [
  {
    method: 'GET',
    path: '/api/v2/admin/users/list',
    description: 'Retrieve a list of users (admin), optionally filtered by tenant',
    requiredRole: 'admin',
    queryParams: [{ name: 'tenantId', description: 'Filter users by tenant ID', required: false }],
    sampleParams: {
      tenantId: '',
    },
    sampleResponse: {
      success: true,
      data: [
        {
          id: 'user123',
          email: 'user@example.com',
          role: 'user',
          tenantId: 'tenant123',
          createdAt: '2023-01-01T00:00:00',
        },
      ],
    },
  },
  {
    method: 'POST',
    path: '/api/users/create',
    description: 'Create a new user',
    requiredRole: 'admin or tenant_admin',
    requestBody: true,
    sampleBody: {
      email: 'newuser@example.com',
      password: 'securepassword',
      role: 'user',
      displayName: 'New User',
      tenantId: 'tenant123',
    },
    sampleResponse: {
      success: true,
      message: 'User created successfully',
      userId: 'newuser123',
    },
  },
  {
    method: 'PUT',
    path: '/api/users/update/:userId',
    description: "Update an existing user's information",
    requiredRole: 'admin or tenant_admin',
    requestBody: true,
    sampleBody: {
      displayName: 'Updated Name',
      role: 'admin',
    },
    sampleResponse: {
      success: true,
      message: 'User updated successfully',
    },
  },
  {
    method: 'DELETE',
    path: '/api/users/delete/:userId',
    description: 'Delete a user',
    requiredRole: 'admin or tenant_admin',
    sampleResponse: {
      success: true,
      message: 'User deleted successfully',
    },
  },
  {
    method: 'GET',
    path: '/api/users/by-email',
    description: 'Retrieve a user by their email address',
    requiredRole: 'admin or tenant_admin',
    queryParams: [
      { name: 'email', description: 'Email address of the user to find', required: true },
    ],
    sampleParams: {
      email: 'user@example.com',
    },
    sampleResponse: {
      success: true,
      data: {
        id: 'user123',
        email: 'user@example.com',
        role: 'user',
        tenantId: 'tenant123',
      },
    },
  },
];

// TENANT MANAGEMENT ENDPOINTS
export const tenantApiEndpoints = [
  {
    method: 'GET',
    path: '/api/v2/admin/tenants/list',
    description: 'Retrieve a list of all tenants',
    requiredRole: 'admin',
    sampleResponse: [
      {
        id: 'tenant123',
        name: 'Example Tenant',
        createdAt: '2023-01-01T00:00:00',
      },
    ],
  },
  {
    method: 'POST',
    path: '/api/v2/admin/tenants',
    description: 'Create a new tenant',
    requiredRole: 'admin',
    requestBody: true,
    sampleBody: {
      name: 'New Tenant',
      settings: {
        maxUsers: 10,
      },
    },
    sampleResponse: {
      success: true,
      message: 'Tenant created successfully',
      tenantId: 'newtenant123',
    },
  },
  {
    method: 'PATCH',
    path: '/api/v2/admin/tenants/:tenantId',
    description: 'Update an existing tenant',
    requiredRole: 'admin',
    requestBody: true,
    sampleBody: {
      name: 'Updated Tenant Name',
      settings: {
        maxUsers: 20,
      },
    },
    sampleResponse: {
      success: true,
      message: 'Tenant updated successfully',
    },
  },
  {
    method: 'DELETE',
    path: '/api/v2/admin/tenants/:tenantId',
    description: 'Delete a tenant',
    requiredRole: 'admin',
    sampleResponse: {
      success: true,
      message: 'Tenant deleted successfully',
    },
  },
];

// ALERT MANAGEMENT ENDPOINTS
export const alertApiEndpoints = [
  {
    method: 'POST',
    path: '/api/alerts/send',
    description: 'Send a new alert and execute associated event flows',
    requiredRole: 'responder, tenant_admin, support_user, overseer, or admin',
    requestBody: true,
    sampleBody: {
      tenantId: 'tenant123',
      title: 'Critical Server Outage',
      message: 'Production server is down',
      severity: 'critical',
      source: 'server-monitor',
      tags: ['server', 'production'],
      metadata: { server_id: 'srv-01' },
    },
    sampleResponse: {
      success: true,
      alertId: 'generated-alert-id',
    },
  },
  {
    method: 'POST',
    path: '/api/alert/test',
    description: 'Test an alert (no persistence)',
    requiredRole: 'responder, tenant_admin, support_user, overseer, or admin',
    requestBody: true,
    sampleBody: {
      tenantId: 'tenant123',
      title: 'Test Alert',
      message: 'This is a test alert',
    },
    sampleResponse: {
      success: true,
      message: 'Alert test processed successfully. No actual alerts were created.',
      alertId: 'test-alert-uuid',
    },
  },
  {
    method: 'POST',
    path: '/api/alerts/acknowledge',
    description: 'Acknowledge an alert',
    requiredRole: 'responder, tenant_admin, support_user, overseer, or admin',
    requestBody: true,
    sampleBody: {
      tenantId: 'tenant123',
      alertId: 'alert123',
      userId: 'user123',
      notes: 'Acknowledged by on-call engineer',
    },
    sampleResponse: {
      success: true,
      message: 'Acknowledgment recorded',
    },
  },
];

// ACTION MANAGEMENT ENDPOINTS
export const actionApiEndpoints = [
  {
    method: 'GET',
    path: '/actions/list',
    description: 'List all actions for a tenant',
    requiredRole: 'tenant_admin or admin',
    queryParams: [
      { name: 'tenantId', description: 'The ID of the tenant', required: true },
      { name: 'showDisabled', description: 'Whether to include disabled actions', required: false },
    ],
    sampleParams: {
      tenantId: 'tenant123',
      showDisabled: 'false',
    },
    sampleResponse: {
      success: true,
      data: [
        {
          id: 'action123',
          label: 'SMS Alert',
          type: 'twilio_sms',
          enabled: true,
          messageTemplate: 'Alert: {message}',
        },
      ],
    },
  },
  {
    method: 'POST',
    path: '/actions/create',
    description: 'Create a new action',
    requiredRole: 'tenant_admin or admin',
    requestBody: true,
    sampleBody: {
      tenantId: 'tenant123',
      label: 'SMS Alert',
      type: 'twilio_sms',
      messageTemplate: 'Alert: {message}',
      enabled: true,
    },
    sampleResponse: {
      success: true,
      message: 'Action created successfully',
      actionId: 'action123',
    },
  },
  {
    method: 'POST',
    path: '/actions/dispatch',
    description: 'Trigger an action',
    requiredRole: 'tenant_admin or admin',
    requestBody: true,
    sampleBody: {
      tenantId: 'tenant123',
      actionId: 'action123',
      context: {
        alertId: 'alert123',
        message: 'Intrusion detected',
      },
    },
    sampleResponse: {
      success: true,
      message: 'Action dispatched successfully',
    },
  },
];

// EVENT MANAGEMENT ENDPOINTS
export const eventApiEndpoints = [
  {
    method: 'GET',
    path: '/events/list',
    description: 'List all events for a tenant',
    requiredRole: 'user (any authenticated user)',
    queryParams: [{ name: 'tenantId', description: 'The ID of the tenant', required: true }],
    sampleParams: {
      tenantId: 'tenant123',
    },
    sampleResponse: {
      success: true,
      data: [
        {
          id: 'event123',
          eventType: 'alert_triggered',
          status: 'success',
          message: 'Alert sent to all responders',
          timestamp: '2023-01-01T00:00:00',
        },
      ],
    },
  },
  {
    method: 'POST',
    path: '/events/create',
    description: 'Create a new event',
    requiredRole: 'user (any authenticated user)',
    requestBody: true,
    sampleBody: {
      tenantId: 'tenant123',
      eventType: 'alert_triggered',
      status: 'success',
      message: 'Alert sent to all responders',
      triggeredBy: 'system',
    },
    sampleResponse: {
      success: true,
      eventId: 'event123',
    },
  },
];

// WEBHOOK MANAGEMENT ENDPOINTS
export const webhookApiEndpoints = [
  {
    method: 'GET',
    path: '/api/webhooks/list',
    description: 'List all webhook actions for a tenant',
    requiredRole: 'tenant_admin or admin',
    queryParams: [{ name: 'tenantId', description: 'The ID of the tenant', required: true }],
    sampleParams: {
      tenantId: 'tenant123',
    },
    sampleResponse: {
      success: true,
      data: [
        {
          id: 'webhook123',
          label: 'External Notification',
          url: 'https://example.com/webhook',
          enabled: true,
        },
      ],
    },
  },
  {
    method: 'POST',
    path: '/api/webhooks/create',
    description: 'Create a new webhook action',
    requiredRole: 'tenant_admin or admin',
    requestBody: true,
    sampleBody: {
      tenantId: 'tenant123',
      label: 'External Notification',
      url: 'https://example.com/webhook',
      enabled: true,
      messageTemplate: {
        alert: '{alertId}',
        message: '{message}',
        timestamp: '{timestamp}',
      },
      headers: {
        Authorization: 'Bearer your-api-key',
      },
    },
    sampleResponse: {
      success: true,
      message: 'Webhook created successfully',
      webhookId: 'webhook123',
    },
  },
];

// REST ACTION MANAGEMENT ENDPOINTS
export const restActionApiEndpoints = [
  {
    method: 'GET',
    path: '/api/rest-actions/list',
    description: 'List all REST actions for a tenant',
    requiredRole: 'tenant_admin or admin',
    queryParams: [{ name: 'tenantId', description: 'The ID of the tenant', required: true }],
    sampleParams: {
      tenantId: 'tenant123',
    },
    sampleResponse: {
      success: true,
      data: [
        {
          id: 'rest123',
          label: 'Update Status',
          targetUrl: 'https://api.example.com/status',
          method: 'POST',
          enabled: true,
        },
      ],
    },
  },
  {
    method: 'POST',
    path: '/api/rest-actions/create',
    description: 'Create a new REST action',
    requiredRole: 'tenant_admin or admin',
    requestBody: true,
    sampleBody: {
      tenantId: 'tenant123',
      label: 'Update Status',
      targetUrl: 'https://api.example.com/status',
      method: 'POST',
      payloadTemplate: {
        status: 'alert_triggered',
        alertId: '{alertId}',
        timestamp: '{timestamp}',
      },
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer your-api-key',
      },
      enabled: true,
    },
    sampleResponse: {
      success: true,
      message: 'REST action created successfully',
      actionId: 'rest123',
    },
  },
];
